import { useEffect, useState } from "react";
import { api, asset } from "../lib/api";
import { useText } from "../lib/siteText";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const Tile = ({ it, rowH, onOpen, i }) => {
  const ar = it.w / it.h;
  return (
    <button data-testid={`legacy-tile-${i}`} onClick={() => onOpen(it)} className="group relative overflow-hidden bg-obsidian-800"
      style={{ width: `${ar * rowH}px`, flexGrow: ar }}>
      <div style={{ paddingBottom: `${100 / ar}%` }} />
      <img src={asset(it.url)} alt={it.caption} loading="lazy" className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-[1.03]" />
      <div className="absolute inset-x-0 bottom-0 p-3 text-left bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        {it.year && <div className="font-mono text-[10px] text-amber-glow">{it.year}</div>}
        <div className="text-xs sm:text-sm text-white leading-snug">{it.caption}</div>
      </div>
    </button>
  );
};

const Sprockets = ({ pos }) => (
  <div className={`absolute inset-x-0 ${pos} h-2.5 pointer-events-none`} style={{ background: "repeating-linear-gradient(90deg, transparent 0 10px, rgba(255,255,255,0.18) 10px 22px)" }} />
);

const FilmStrip = ({ frames, onOpen }) => (
  <div data-testid="legacy-crash-strip" className="bg-black overflow-x-auto">
    <div className="relative py-5 min-w-max lg:min-w-0">
      <Sprockets pos="top-1" />
      <Sprockets pos="bottom-1" />
      <div className="flex gap-1.5 px-1.5">
      {frames.map((f, i) => (
        <button key={f.id} data-testid={`crash-frame-${i}`} onClick={() => onOpen(f)} className="relative flex-1 min-w-[200px] lg:min-w-0 bg-obsidian-800">
          <div style={{ paddingBottom: `${(f.h / f.w) * 100}%` }} />
          <img src={asset(f.url)} alt={f.caption} loading="lazy" className="absolute inset-0 w-full h-full" />
          <span className="absolute bottom-1 left-1.5 font-mono text-[10px] text-amber-glow bg-black/60 px-1 rounded">{String(i + 1).padStart(2, "0")}</span>
        </button>
      ))}
      </div>
    </div>
  </div>
);

export const LegacySection = ({ limit = 12, testid = "legacy-section" }) => {
  const t = useText();
  const [items, setItems] = useState([]);
  const [press, setPress] = useState([]);
  const [open, setOpen] = useState(null);
  const [all, setAll] = useState(false);
  const [rowH, setRowH] = useState(240);

  useEffect(() => {
    api.get("/legacy").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/incognito/assets").then(({ data }) => setPress(data.filter((a) => a.kind === "press")
      .map((a) => ({ id: a.id, url: a.url, w: 3, h: 4, caption: [a.outlet, a.title].filter(Boolean).join(" — "), group: "origin" })))).catch(() => {});
    const fit = () => setRowH(window.innerWidth < 640 ? 120 : window.innerWidth < 1024 ? 180 : 240);
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  if (!items.length) return null;
  const showcase = items.filter((i) => i.group === "showcase");
  const crash = items.filter((i) => i.group === "crash");
  const origin = [...items.filter((i) => i.group === "origin"), ...press];
  const shown = all ? showcase : showcase.slice(0, limit);
  const credits = t("inc_hire_list").split("\n").filter((l) => l.trim());

  return (
    <section data-testid={testid} className="bg-obsidian">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
        <div className="overline-label mb-4 text-amber-glow">1992 — Now</div>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] max-w-4xl">Front row at history.</h2>
        <div className="mt-6 flex flex-wrap gap-x-3 gap-y-2 text-sm sm:text-base text-neutral-300">
          {credits.map((c, i) => (
            <span key={i} data-testid={`legacy-credit-${i}`} className="flex items-center gap-3">{i > 0 && <span className="text-amber-glow/60">·</span>}{c}</span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 px-1.5">
        {shown.map((it, i) => <Tile key={it.id} it={it} rowH={rowH} onOpen={setOpen} i={i} />)}
      </div>
      {showcase.length > shown.length && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-8">
          <button data-testid="legacy-show-all" onClick={() => setAll(true)}
            className="border border-white/20 rounded-full px-6 py-2.5 text-sm hover:border-amber-glow hover:text-amber-glow transition-colors">
            See all {showcase.length} moments
          </button>
        </div>
      )}

      {crash.length > 0 && (
        <div className="mt-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-6">
            <div className="overline-label mb-2">Daytona 500 · 2020</div>
            <h3 className="font-serif text-3xl lg:text-4xl font-light">The last lap, frame by frame.</h3>
          </div>
          <FilmStrip frames={crash} onOpen={setOpen} />
        </div>
      )}

      {origin.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-[1fr,1.4fr] gap-12 items-center">
          <div>
            <div className="overline-label mb-3">Where it started</div>
            <h3 className="font-serif text-3xl lg:text-5xl font-light leading-tight">Las Vegas. Age 11.</h3>
            <p className="mt-5 text-neutral-400 max-w-md">{t("inc_hire_intro")}</p>
          </div>
          <div className="flex gap-3 items-end">
            {origin.map((o, i) => (
              <button key={o.id} data-testid={`legacy-origin-${i}`} onClick={() => setOpen(o)} className="relative bg-obsidian-800 border border-white/10 rounded-md p-2"
                style={{ flex: `${o.w / o.h} 1 0` }}>
                <img src={asset(o.url)} alt={o.caption} className="w-full h-auto rounded-sm" />
                <div className="text-xs text-neutral-400 mt-2 text-left">{o.caption}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="legacy-lightbox" className="max-w-5xl w-[96vw] bg-obsidian-800 border-white/10 p-4">
          <DialogTitle className="sr-only">{open?.caption}</DialogTitle>
          {open && (
            <>
              <img src={asset(open.url)} alt={open.caption} className="w-full max-h-[78vh] object-contain" />
              <div className="mt-3 flex gap-3 items-baseline">
                {open.year && <span className="font-mono text-xs text-amber-glow">{open.year}</span>}
                <span className="text-sm text-neutral-200">{open.caption}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
